import { escapeUserText } from "../../utils/mentions";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Check,
  GraduationCap,
  Mail,
  MapPin,
  MessageCircle,
  Share2,
} from "lucide-react";
import Header from "../headers/headerfornews";
import { EventMediaDisplay } from "./eventMediaDisplay";
import "./EventDetails.css";

const createLinkifiedText = (text = "") =>
  escapeUserText(text).replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

const formatEventDate = (value) => {
  if (!value) return "TBA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatLocation = (event) => {
  const location = (event.location || "").trim();
  const country = (event.country || "").trim();
  if (location && country && location.toLowerCase() !== country.toLowerCase()) {
    return `${location}, ${country}`;
  }
  return location || country || "TBA";
};

const BULLET_RE = /^(?:[✓✔✅☑●•*]|\u2713|\u2714|-)\s+/;
const LABEL_RE = /^([^:\n]{2,48}):\s+(.+)$/;

const isContactLine = (line = "") =>
  /whatsapp|email|contact us via|@|\b\d{7,}\b/i.test(line);

const isInfoLine = (line = "") =>
  /don'?t miss|opportunity|courses?|guided projects|professional certificates|scan the qr/i.test(
    line
  );

/**
 * Split description into intro, checklist rows, and callout blocks
 * so the detail card can mirror the flyer-style layout.
 */
const parseDescription = (raw = "") => {
  const lines = String(raw)
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const intro = [];
  const checklist = [];
  const callouts = [];

  lines.forEach((line) => {
    const cleaned = line.replace(BULLET_RE, "").trim();
    const labelMatch = cleaned.match(LABEL_RE);
    const looksLikeCheck =
      BULLET_RE.test(line) ||
      (labelMatch &&
        /period|date|start|enrol|induct|subscription|registration/i.test(
          labelMatch[1]
        ));

    if (looksLikeCheck) {
      checklist.push({
        label: labelMatch ? labelMatch[1] : null,
        value: labelMatch ? labelMatch[2] : cleaned,
        raw: cleaned,
      });
      return;
    }

    if (isContactLine(cleaned)) {
      callouts.push({ type: "contact", text: cleaned });
      return;
    }

    if (isInfoLine(cleaned) || checklist.length > 0) {
      callouts.push({ type: "info", text: cleaned });
      return;
    }

    intro.push(cleaned);
  });

  return { intro, checklist, callouts };
};

const EventDetails = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    message: "",
  });

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetch(`${API_URL}/get_event_activities.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          const found = data.data.find((ev) => String(ev.id) === String(id));
          setEvent(found || null);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching event details:", err);
        setLoading(false);
      });
  }, [id, API_URL]);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/events/${event.id}`;
    const shareText = `Check out this event: ${event.event_name}`;

    if (navigator.share) {
      navigator
        .share({
          title: event.event_name,
          text: shareText,
          url: shareUrl,
        })
        .catch((err) => {
          console.warn("Native share failed:", err);
          window.open(shareUrl, "_blank");
        });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.contact ||
      !formData.message
    ) {
      alert("Please fill out all fields.");
      return;
    }

    setContactLoading(true);
    try {
      const response = await fetch(`${API_URL}/contact_submit.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        alert("Thank you for reaching out! We’ll get back to you soon.");
        setFormData({ name: "", email: "", contact: "", message: "" });
        setShowContactModal(false);
      } else {
        alert("Error: " + result.message);
      }
    } catch (error) {
      console.error("Contact form submission failed:", error);
      alert("Something went wrong. Please try again later.");
    } finally {
      setContactLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <p className="ed-loading">Loading event details...</p>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <Header />
        <p className="ed-loading">Event not found.</p>
      </>
    );
  }

  const { intro, checklist, callouts } = parseDescription(event.description || "");
  const fallbackDescription =
    !intro.length && !checklist.length && !callouts.length
      ? event.description || ""
      : "";

  return (
    <>
      <Header />
      <div className="ed-page">
        <div className="ed-shell">
          <div className="ed-hero">
            <EventMediaDisplay
              event={event}
              apiUrl={API_URL}
              mediaStyle={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "contain",
              }}
              noMediaContent={
                <div className="ed-hero-empty">No media available</div>
              }
            />
          </div>

          <article className="ed-card">
            <div className="ed-brand">
              <span className="ed-brand-icon" aria-hidden="true">
                <GraduationCap size={22} strokeWidth={2.2} />
              </span>
              <h1 className="ed-brand-title">{event.event_name}</h1>
            </div>

            {intro.length > 0 && (
              <p
                className="ed-intro"
                dangerouslySetInnerHTML={{
                  __html: createLinkifiedText(intro.join("\n\n")),
                }}
              />
            )}

            {fallbackDescription && (
              <p
                className="ed-intro"
                dangerouslySetInnerHTML={{
                  __html: createLinkifiedText(fallbackDescription),
                }}
              />
            )}

            {checklist.length > 0 && (
              <ul className="ed-checklist">
                {checklist.map((item, index) => (
                  <li key={`${item.raw}-${index}`} className="ed-check-item">
                    <span className="ed-check-icon" aria-hidden="true">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>
                      {item.label ? (
                        <>
                          <strong>{item.label}:</strong> {item.value}
                        </>
                      ) : (
                        item.raw
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {callouts.map((callout, index) => (
              <div key={`${callout.type}-${index}`} className="ed-callout">
                <span
                  className={`ed-callout-icon ${
                    callout.type === "contact" ? "is-contact" : "is-info"
                  }`}
                  aria-hidden="true"
                >
                  {callout.type === "contact" ? (
                    <MessageCircle size={18} strokeWidth={2.2} />
                  ) : (
                    <BookOpen size={18} strokeWidth={2.2} />
                  )}
                </span>
                <p
                  style={{ margin: 0 }}
                  dangerouslySetInnerHTML={{
                    __html: createLinkifiedText(callout.text),
                  }}
                />
              </div>
            ))}

            <div className="ed-meta-bar">
              <div className="ed-meta-half">
                <Calendar size={16} strokeWidth={2.2} />
                <span className="ed-meta-label">Date:</span>
                <span className="ed-meta-value">
                  {formatEventDate(event.event_date)}
                </span>
              </div>
              <div className="ed-meta-half">
                <MapPin size={16} strokeWidth={2.2} />
                <span className="ed-meta-label">Location:</span>
                <span className="ed-meta-value">{formatLocation(event)}</span>
              </div>
            </div>
          </article>

          <div className="ed-actions">
            <button
              type="button"
              className="ed-btn ed-btn-primary"
              onClick={() => setShowContactModal(true)}
            >
              <Mail size={16} strokeWidth={2.2} />
              Contact Us
            </button>
            <button
              type="button"
              className="ed-btn ed-btn-outline"
              onClick={handleShare}
            >
              <Share2 size={16} strokeWidth={2.2} />
              Share
            </button>
            <Link to="/" className="ed-btn ed-btn-muted">
              <ArrowLeft size={16} strokeWidth={2.2} />
              Back
            </Link>
          </div>
        </div>
      </div>

      {showContactModal && (
        <div className="ed-modal-overlay">
          <div className="ed-modal">
            <h3>Contact Us</h3>
            <form onSubmit={handleSubmitContact} className="ed-form">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <input
                type="text"
                name="contact"
                placeholder="Contact Number"
                value={formData.contact}
                onChange={handleInputChange}
                required
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                required
              />
              <div className="ed-modal-actions">
                <button
                  type="submit"
                  className="ed-modal-submit"
                  disabled={contactLoading}
                  style={{ opacity: contactLoading ? 0.7 : 1 }}
                >
                  {contactLoading ? "Submitting..." : "Submit"}
                </button>
                <button
                  type="button"
                  className="ed-modal-cancel"
                  onClick={() => setShowContactModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default EventDetails;
